// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState } from 'react';
import type { PubmedPaperDetails } from '../../../../../services/pubmedService';
import type { AbstractTriageBrief } from '../../../lib/models';
import { INPUT_SOURCE_LABELS, CONFIDENCE_CONFIG } from '../constants/triageConstants';

export interface ExtractedParameter {
  name: string;
  value: string | number;
  valueDetails?: Array<{ label: string; value: string }>;
  confidence: 'low' | 'medium' | 'high';
  context: string;
  source: 'abstract' | 'title' | 'metadata';
  category: 'efficacy' | 'safety' | 'cost' | 'utility' | 'epidemiology';
}

export interface StudySummaryData {
  relevanceExplanation: string;
  extractedParameters: ExtractedParameter[];
  methodologyHighlights: string[];
  keyFindings: string[];
  limitations: string[];
  applicabilityNotes: string[];
}

export interface EnhancedStudySummaryProps {
  paper: PubmedPaperDetails;
  brief: AbstractTriageBrief;
}

const MAX_CONTEXT_LENGTH = 220;

/**
 * Enhanced study summary component with categorized information display
 * Provides detailed relevant information when users click on analyzed studies
 */
export const EnhancedStudySummary: React.FC<EnhancedStudySummaryProps> = ({
  paper,
  brief
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['relevance']));

  // Generate enhanced summary data from the brief
  const summaryData = generateSummaryData(brief, paper);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const highlightTextInAbstract = (_text: string) => {
    // Note: This function is called when users click on parameters/findings/methodology
    // The highlighting will be handled by the parent Evidence Drawer component
    // which shows the paper expand indicator to view the full abstract
  };

  return (
    <div className="enhanced-study-summary">
      {/* Relevance Overview Section */}
      <div className="enhanced-study-summary__section">
        <button
          className="enhanced-study-summary__section-header"
          onClick={() => toggleSection('relevance')}
          aria-expanded={expandedSections.has('relevance')}
        >
          <span className="enhanced-study-summary__section-icon">
            {expandedSections.has('relevance') ? 'v' : '>'}
          </span>
          <h4 className="enhanced-study-summary__section-title">
            Relevance Overview
          </h4>
          <div className="enhanced-study-summary__confidence-indicator">
            <span className={`enhanced-study-summary__confidence enhanced-study-summary__confidence--${CONFIDENCE_CONFIG[brief.confidence].color}`}>
              {CONFIDENCE_CONFIG[brief.confidence].icon} {CONFIDENCE_CONFIG[brief.confidence].text}
            </span>
          </div>
        </button>
        
        {expandedSections.has('relevance') && (
          <div className="enhanced-study-summary__section-content">
            <p className="enhanced-study-summary__relevance-text">
              {summaryData.relevanceExplanation}
            </p>
            
            {brief.potentialInputSources.length > 0 && (
              <div className="enhanced-study-summary__input-sources">
                <h5 className="enhanced-study-summary__subsection-title">Relevant for:</h5>
                <div className="enhanced-study-summary__tags">
                  {brief.potentialInputSources.map(source => (
                    <span key={source} className="enhanced-study-summary__tag">
                      {INPUT_SOURCE_LABELS[source] || source}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {summaryData.keyFindings.length > 0 && (
              <div className="enhanced-study-summary__relevance-block">
                <h5 className="enhanced-study-summary__subsection-title">Key Findings</h5>
                <ul className="enhanced-study-summary__list">
                  {summaryData.keyFindings.map((finding, index) => (
                    <li key={index} className="enhanced-study-summary__list-item">
                      {finding}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {summaryData.applicabilityNotes.length > 0 && (
              <div className="enhanced-study-summary__relevance-block">
                <h5 className="enhanced-study-summary__subsection-title">Applicability to Analysis</h5>
                <ul className="enhanced-study-summary__list">
                  {summaryData.applicabilityNotes.map((note, index) => (
                    <li key={index} className="enhanced-study-summary__list-item">
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Extracted Parameters Section */}
      {summaryData.extractedParameters.length > 0 && (
        <div className="enhanced-study-summary__section">
          <button
            className="enhanced-study-summary__section-header"
            onClick={() => toggleSection('parameters')}
            aria-expanded={expandedSections.has('parameters')}
          >
            <span className="enhanced-study-summary__section-icon">
              {expandedSections.has('parameters') ? 'v' : '>'}
            </span>
            <h4 className="enhanced-study-summary__section-title">
              Extracted Parameters ({summaryData.extractedParameters.length})
            </h4>
          </button>
          
          {expandedSections.has('parameters') && (
            <div className="enhanced-study-summary__section-content">
              <div className="enhanced-study-summary__parameters-grid">
                {summaryData.extractedParameters.map((param, index) => (
                  <div key={index} className="enhanced-study-summary__parameter-card">
                    <div className="enhanced-study-summary__parameter-header">
                      <span className="enhanced-study-summary__parameter-name">
                        {param.name}
                      </span>
                      <span className={`enhanced-study-summary__parameter-confidence enhanced-study-summary__parameter-confidence--${param.confidence}`}>
                        {param.confidence}
                      </span>
                    </div>
                    
                    <div className="enhanced-study-summary__parameter-value">
                      {param.value}
                    </div>

                    {param.valueDetails && param.valueDetails.length > 0 && (
                      <div className="enhanced-study-summary__parameter-value-details">
                        {param.valueDetails.map((detail, index) => (
                          <div key={`${detail.label}-${index}`} className="enhanced-study-summary__parameter-value-detail">
                            <span className="enhanced-study-summary__parameter-value-label">
                              {detail.label}:
                            </span>
                            <span className="enhanced-study-summary__parameter-value-text">
                              {detail.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="enhanced-study-summary__parameter-context">
                      <span className="enhanced-study-summary__context-button">
                        "{truncateText(param.context, MAX_CONTEXT_LENGTH)}"
                      </span>
                    </div>
                    
                    <div className="enhanced-study-summary__parameter-category">
                      {param.category}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Methodology Highlights Section */}
      {summaryData.methodologyHighlights.length > 0 && (
        <div className="enhanced-study-summary__section">
          <button
            className="enhanced-study-summary__section-header"
            onClick={() => toggleSection('methodology')}
            aria-expanded={expandedSections.has('methodology')}
          >
            <span className="enhanced-study-summary__section-icon">
              {expandedSections.has('methodology') ? 'v' : '>'}
            </span>
            <h4 className="enhanced-study-summary__section-title">
              Methodology Highlights
            </h4>
          </button>
          
          {expandedSections.has('methodology') && (
            <div className="enhanced-study-summary__section-content">
              <ul className="enhanced-study-summary__list">
                {summaryData.methodologyHighlights.map((highlight, index) => (
                  <li key={index} className="enhanced-study-summary__list-item">
                    <button
                      className="enhanced-study-summary__highlight-button"
                      onClick={() => highlightTextInAbstract(highlight)}
                    >
                      {highlight}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {/* Limitations Section */}
      {summaryData.limitations.length > 0 && (
        <div className="enhanced-study-summary__section">
          <button
            className="enhanced-study-summary__section-header"
            onClick={() => toggleSection('limitations')}
            aria-expanded={expandedSections.has('limitations')}
          >
            <span className="enhanced-study-summary__section-icon">
              {expandedSections.has('limitations') ? 'v' : '>'}
            </span>
            <h4 className="enhanced-study-summary__section-title">
              Limitations & Cautions
            </h4>
          </button>
          
          {expandedSections.has('limitations') && (
            <div className="enhanced-study-summary__section-content">
              <ul className="enhanced-study-summary__list enhanced-study-summary__list--cautions">
                {summaryData.limitations.map((limitation, index) => (
                  <li key={index} className="enhanced-study-summary__list-item enhanced-study-summary__list-item--caution">
                    {limitation}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const trimmed = text.slice(0, Math.max(0, maxLength - 3)).trimEnd();
  return `${trimmed}...`;
}

/**
 * Generate enhanced summary data from AI brief and paper details
 */
function generateSummaryData(brief: AbstractTriageBrief, paper: PubmedPaperDetails): StudySummaryData {
  // Generate relevance explanation
  const relevanceExplanation = generateRelevanceExplanation(brief);
  
  // Extract parameters from brief (mock implementation - would be enhanced with real data)
  const extractedParameters = generateExtractedParameters(brief, paper);
  
  // Generate methodology highlights from summary
  const methodologyHighlights = extractMethodologyHighlights(brief.summary);
  
  // Generate key findings
  const keyFindings = extractKeyFindings(brief.summary);
  
  // Generate limitations from cautions and analysis
  const limitations = generateLimitations(brief);
  
  // Generate applicability notes
  const applicabilityNotes = generateApplicabilityNotes(brief);

  return {
    relevanceExplanation,
    extractedParameters,
    methodologyHighlights,
    keyFindings,
    limitations,
    applicabilityNotes
  };
}

function generateRelevanceExplanation(brief: AbstractTriageBrief): string {
  if (brief.potentialInputSources.length === 0 || brief.potentialInputSources.includes('none')) {
    return "This study was analyzed but did not contain specific parameters relevant to vaccine modeling. It may provide general context or background information.";
  }

  const sourceLabels = brief.potentialInputSources
    .filter(source => source !== 'none')
    .map(source => INPUT_SOURCE_LABELS[source] || source);

  if (sourceLabels.length === 1) {
    return `This study is relevant because it provides information about ${sourceLabels[0].toLowerCase()} that can be used in vaccine modeling.`;
  } else if (sourceLabels.length === 2) {
    return `This study is relevant because it provides information about ${sourceLabels[0].toLowerCase()} and ${sourceLabels[1].toLowerCase()} for vaccine modeling.`;
  } else {
    const lastSource = sourceLabels.pop();
    return `This study is relevant because it provides information about ${sourceLabels.join(', ').toLowerCase()}, and ${lastSource?.toLowerCase()} for vaccine modeling.`;
  }
}

function generateExtractedParameters(brief: AbstractTriageBrief, paper: PubmedPaperDetails): ExtractedParameter[] {
  const parameters: ExtractedParameter[] = [];
  const retrievableInputs = brief.retrievable_inputs ?? [];

  retrievableInputs.forEach(input => {
    const displayName = getDisplayNameForInputKey(input.input_key);
    if (!displayName) return;

    const valueInfo = getValueFromSupportingText(input.supporting_text);
    const value = valueInfo.value || getLikelihoodLabel(input.likelihood);
    const confidence = mapLikelihoodToConfidence(input.likelihood);
    const context = input.supporting_text || input.applicability_notes || brief.summary || `Referenced in abstract analysis for "${paper.title}".`;
    const source = mapBasisToSource(input.basis);
    const category = getCategoryForInputKey(input.input_key);
    const valueDetails = getDisplayValueDetails(valueInfo.details);

    parameters.push({
      name: displayName,
      value,
      valueDetails,
      confidence,
      context,
      source,
      category
    });
  });

  if (parameters.length === 0) {
    brief.potentialInputSources
      .filter(source => source !== 'none')
      .forEach(source => {
        parameters.push({
          name: INPUT_SOURCE_LABELS[source] || source,
          value: 'Potential evidence',
          confidence: brief.confidence,
          context: brief.summary || `Potentially relevant based on abstract analysis for "${paper.title}".`,
          source: 'abstract',
          category: getCategoryForInputSource(source)
        });
      });
  }

  return parameters;
}

function mapLikelihoodToConfidence(likelihood: string): 'low' | 'medium' | 'high' {
  switch (likelihood) {
    case 'explicit_in_abstract':
      return 'high';
    case 'likely_in_full_text':
      return 'medium';
    case 'uncertain':
    case 'unlikely':
      return 'low';
    default:
      return 'medium';
  }
}

function getLikelihoodLabel(likelihood: string): string {
  switch (likelihood) {
    case 'explicit_in_abstract':
      return 'Explicit in abstract';
    case 'likely_in_full_text':
      return 'Likely in full text';
    case 'uncertain':
      return 'Uncertain';
    case 'unlikely':
      return 'Unlikely';
    default:
      return 'Unspecified';
  }
}

function mapBasisToSource(basis?: string): 'abstract' | 'title' | 'metadata' {
  if (basis === 'title') return 'title';
  if (basis === 'mesh' || basis === 'pub_type') return 'metadata';
  return 'abstract';
}

function getDisplayNameForInputKey(inputKey: string): string | null {
  const mapping: Record<string, string> = {
    efficacy: 'Vaccine Efficacy',
    vaccine_efficacy: 'Vaccine Efficacy',
    vaccineEfficacyPct: 'Vaccine efficacy (%)',
    waning: 'Waning',
    waning_rate: 'Waning Rate',
    waningPctPerYear: 'Waning rate (%/year)',
    incidence: 'Baseline Incidence',
    baseline_incidence: 'Baseline Incidence',
    baselineIncidencePer100k: 'Baseline incidence (/100k)',
    cost: 'Costs / Resource Use',
    treatment_cost: 'Treatment Cost',
    treatmentCostPerCaseEUR: 'Treatment cost per case',
    vaccine_cost: 'Vaccine Cost',
    vaccineCostPerDoseEUR: 'Vaccine cost per dose',
    admin_cost: 'Administration Cost',
    adminCostPerDoseEUR: 'Administration cost per dose',
    hospitalization_cost: 'Hospitalization Cost',
    hospitalizationCostPerCaseEUR: 'Hospitalization cost per case',
    productivity_loss: 'Productivity Loss',
    productivityValuePerDayEUR: 'Productivity value per day',
    safety: 'Safety Outcomes',
    adverse_event_rate: 'Adverse Event Rate',
    adverseEventRate: 'Adverse event rate',
    adverse_event_cost: 'Adverse Event Cost',
    adverseEventCost: 'Adverse event cost',
    adverse_event_qaly_loss: 'Adverse Event QALY Loss',
    adverseEventQalyLoss: 'Adverse event QALY loss',
    utility: 'Utilities / QoL',
    utility_infection: 'Utility (Infection)',
    utilityInfection: 'Utility during infection',
    utility_hospitalization: 'Utility (Hospitalization)',
    utilityHospitalization: 'Utility during hospitalization',
    mortality_rate: 'Mortality Rate',
    baselineMortalityRate: 'Infection-related mortality (%)',
    hospitalization_rate: 'Hospitalization Rate',
    hospitalizationRate: 'Hospitalization rate (%)',
    coverage: 'Coverage',
    coverage_rate: 'Coverage Rate',
    defaultCoveragePct: 'Coverage (%)',
    coveragePctByRegion: 'Coverage by region',
    timeHorizonYears: 'Time horizon (years)',
    discountRatePct: 'Discount rate (%)',
    perspective: 'Perspective',
    boosterYears: 'Booster years',
    boosterCost: 'Booster cost'
  };

  if (mapping[inputKey]) return mapping[inputKey];

  const cleaned = inputKey.replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return null;
  return cleaned
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getCategoryForInputKey(inputKey: string): ExtractedParameter['category'] {
  if (inputKey.includes('efficacy') || inputKey.includes('waning')) return 'efficacy';
  if (inputKey.includes('cost') || inputKey.includes('productivity')) return 'cost';
  if (inputKey.includes('utility')) return 'utility';
  if (inputKey.includes('safety') || inputKey.includes('adverse')) return 'safety';
  return 'epidemiology';
}

function getCategoryForInputSource(source: string): ExtractedParameter['category'] {
  switch (source) {
    case 'vaccineEfficacy':
    case 'waningBoosters':
      return 'efficacy';
    case 'costsResourceUse':
      return 'cost';
    case 'utilitiesQoL':
      return 'utility';
    case 'safety':
      return 'safety';
    case 'incidenceEpi':
    case 'coverageAdherence':
    case 'modelStructure':
    default:
      return 'epidemiology';
  }
}

export function getValueFromSupportingText(text?: string): { value: string | null; details: Array<{ label: string; value: string }> } {
  if (!text) return { value: null, details: [] };

  const details: Array<{ label: string; value: string }> = [];
  const rateRegex = /(\d+(?:\.\d+)?)\s*(?:\/|per)\s*100,?000/gi;
  let match: RegExpExecArray | null;

  while ((match = rateRegex.exec(text)) !== null) {
    const value = `${match[1]} per 100,000`;
    const windowStart = Math.max(0, match.index - 20);
    const windowEnd = Math.min(text.length, match.index + match[0].length + 40);
    const windowText = text.slice(windowStart, windowEnd);
    const yearMatches = windowText.match(/\b(19|20)\d{2}\b/g);
    const label = yearMatches && yearMatches.length > 0
      ? yearMatches[yearMatches.length - 1]
      : `Value ${details.length + 1}`;

    details.push({ label, value });
  }

  if (details.length > 0) {
    return { value: details[details.length - 1].value, details };
  }

  const ratePer1000Match = text.match(/(\d+(?:\.\d+)?)\s*(?:\/|per)\s*1000\s*(person-?y|person-?years?)?/i);
  if (ratePer1000Match) {
    const per1000Value = Number(ratePer1000Match[1]);
    if (!Number.isNaN(per1000Value)) {
      const per100k = (per1000Value * 100).toFixed(3).replace(/\.?(0+)$/, '');
      return { value: `${per100k} per 100,000`, details: [] };
    }
  }

  const waningMatch = text.match(/from\s+(\d+(?:\.\d+)?)\s*(?:%|percent)?\s+to\s+(\d+(?:\.\d+)?)\s*(?:%|percent)?(?:[^.]*?\bafter\s+(\d+(?:\.\d+)?)\s*(months?|years?)\b)?/i);
  if (waningMatch) {
    const fromValue = `${waningMatch[1]}%`;
    const toValue = `${waningMatch[2]}%`;
    const timeLabel = waningMatch[3] && waningMatch[4]
      ? `After ${waningMatch[3]} ${waningMatch[4]}`
      : 'Later';

    return {
      value: `${fromValue} -> ${toValue}`,
      details: [
        { label: 'Baseline', value: fromValue },
        { label: timeLabel, value: toValue }
      ]
    };
  }

  const percentRegex = /(-?\d+(?:\.\d+)?)\s*(%|percent)(?![A-Za-z])/gi;
  const percentValues: string[] = [];
  const percentNumbers: number[] = [];
  let percentMatch: RegExpExecArray | null;

  while ((percentMatch = percentRegex.exec(text)) !== null) {
    percentValues.push(`${percentMatch[1]}%`);
    const numericValue = Number(percentMatch[1]);
    if (!Number.isNaN(numericValue)) {
      percentNumbers.push(numericValue);
    }
  }

  const percentRangeMatch = text.match(/-?\d+(?:\.\d+)?\s*%?\s*to\s*-?\d+(?:\.\d+)?\s*%/i);
  const ciPercentMatch = text.match(/\b\d+(?:\.\d+)?%\s*(?:ci|confidence interval)\b/i);

  if (percentValues.length > 0) {
    if (
      percentValues.length > 2 ||
      (percentNumbers.some(value => value < 0) && percentValues.length > 1) ||
      percentRangeMatch ||
      ciPercentMatch
    ) {
      return { value: null, details: [] };
    }
    if (percentValues.length > 1) {
      const percentDetails = percentValues.map((value, index) => ({
        label: `Estimate ${index + 1}`,
        value
      }));
      return { value: percentValues[0], details: percentDetails };
    }

    return { value: percentValues[0], details: [] };
  }

  const rangeMatch = text.match(/\b\d+\s*-\s*\d+\s*(months?|years?)\b/i);
  if (rangeMatch) {
    return { value: null, details: [] };
  }

  const unitMatch = text.match(/(-?\d+(?:\.\d+)?)\s*(EUR|USD|days?|years?|months?)\b/i);
  if (unitMatch) {
    return { value: `${unitMatch[1]} ${unitMatch[2]}`, details: [] };
  }

  const fallbackMatch = text.match(/-?\d+(?:\.\d+)?(?![A-Za-z])/);
  return { value: fallbackMatch ? fallbackMatch[0] : null, details: [] };
}

function getDisplayValueDetails(details: Array<{ label: string; value: string }>): Array<{ label: string; value: string }> | undefined {
  if (details.length > 1) return details;
  if (details.length === 1 && !details[0].label.startsWith('Value')) {
    return details;
  }
  return undefined;
}

function extractMethodologyHighlights(summary: string): string[] {
  const highlights: string[] = [];
  
  // Extract methodology-related phrases from summary
  const methodologyKeywords = [
    'randomized controlled trial',
    'systematic review',
    'meta-analysis',
    'cohort study',
    'case-control',
    'observational study',
    'double-blind',
    'placebo-controlled',
    'follow-up',
    'participants',
    'sample size'
  ];

  methodologyKeywords.forEach(keyword => {
    const regex = new RegExp(`[^.]*${keyword}[^.]*`, 'gi');
    const matches = summary.match(regex);
    if (matches) {
      highlights.push(...matches.map(match => match.trim()));
    }
  });

  return highlights.slice(0, 3); // Limit to top 3 highlights
}

function extractKeyFindings(summary: string): string[] {
  const findings: string[] = [];
  
  // Extract findings-related phrases from summary
  const findingsKeywords = [
    'showed',
    'demonstrated',
    'found',
    'resulted in',
    'significant',
    'reduction',
    'increase',
    'effectiveness',
    'efficacy'
  ];

  findingsKeywords.forEach(keyword => {
    const regex = new RegExp(`[^.]*${keyword}[^.]*`, 'gi');
    const matches = summary.match(regex);
    if (matches) {
      findings.push(...matches.map(match => match.trim()));
    }
  });

  return findings.slice(0, 3); // Limit to top 3 findings
}

function generateLimitations(brief: AbstractTriageBrief): string[] {
  const limitations: string[] = [];
  
  // Add cautions from brief
  if (brief.cautions && brief.cautions.length > 0) {
    limitations.push(...brief.cautions.filter(caution => caution.toLowerCase() !== 'abstract-only'));
  }
  
  // Add confidence-based limitations
  if (brief.confidence === 'low') {
    limitations.push('Low confidence in parameter extraction due to limited information in abstract');
  } else if (brief.confidence === 'medium') {
    limitations.push('Some uncertainty in parameter values - full text review recommended');
  }

  return limitations;
}

function generateApplicabilityNotes(brief: AbstractTriageBrief): string[] {
  const notes: string[] = [];
  
  // Add notes based on potential input sources
  if (brief.potentialInputSources.includes('vaccineEfficacy')) {
    notes.push('Efficacy data may be applicable to similar vaccine programs');
  }
  
  if (brief.potentialInputSources.includes('costsResourceUse')) {
    notes.push('Cost data should be adjusted for local healthcare system and currency');
  }
  
  if (brief.potentialInputSources.includes('incidenceEpi')) {
    notes.push('Epidemiological data may need adjustment for target population demographics');
  }

  // Add general applicability note
  if (notes.length === 0) {
    notes.push('Consider study population, setting, and methodology when applying findings to HTA analysis');
  }

  return notes;
}




